import React, { useEffect, useRef, useState } from "react"
import { Descriptions, Space, Typography } from "antd"

import { AppMetierTitle } from "../../../components/AppMetierTitle/AppMetierTitle"
import { useUpdateHolder } from "../../../queries/HolderQueries"
import { useDebouncedCallback } from "use-debounce"
import {
  getFlushEffectCallback,
  LONG_UPDATE_DEBOUNCE_DEFAULT_DELAY
} from "../../../utils/DebounceUtils"
import {
  AppMetierDateInput,
  SERVER_DATE_FORMAT
} from "../../../components/AppMetierDateInput/AppMetierDateInput"
import dayjs from "dayjs"
import { formatDayjs } from "../../../utils/DayjsUtils"
import { AppMetierSwitch } from "../../../components/AppMetierSwitch"
import { CodeLibelle, HolderRequest } from "../../../client/backend-client/generated"
import { RequiredFieldMessage } from "../project/ProjectForm"
import _ from "lodash"
import { ReferenceSelect } from "../../../components/AppMetierSelect/ReferenceSelect"
import { ReferenceCode, useGetReferenceTyped } from "../../../queries/ReferenceQueries"
import { useGetVintageThreshold } from "../../../queries/VintagesQueries"
import Loader from "../../../components/Loader/Loader"
import { MotifAbandonAccesPlus } from "../../../client/backend-client/motifAbandonAccesPlus"

const LABEL_STYLE = { width: "30%" }

export function AccesPlusForm({
  holderId,
  projectId,
  holder,
  onAccesPlusChange
}: {
  holderId: string
  projectId: number
  holder: HolderRequest
  onAccesPlusChange: (val: boolean) => void
}) {
  const refs = useRef({
    initialHolderRequest: holder
  })

  const { data: motifAbandonRefList } = useGetReferenceTyped<MotifAbandonAccesPlus>(
    ReferenceCode.MOTIF_ABANDON_ACCES_PLUS
  )

  const [holderRequest, setHolderRequest] = useState(refs.current.initialHolderRequest)
  const [millesimeAccesPlus, setMillesimeAccesPlus] = useState(
    refs.current.initialHolderRequest.millesimeAccesPlus
  )

  const { isLoading: areVintagesLoading, data: vintageThreshold } = useGetVintageThreshold(
    dayjs(holderRequest?.dateEntreeAccompagnement).format("YYYY-MM-DD")
  )

  const { data: currentVintageThreshold } = useGetVintageThreshold(dayjs().format("YYYY-MM-DD"))

  const disableAccesPlus = (): boolean => {
    return (
      (holderRequest?.parcoursAccesPlus === false || holderRequest?.parcoursAccesPlus == null) &&
      vintageThreshold?.first === undefined
    )
  }

  const disableAccesPlusDatePicker = (): boolean => {
    return currentVintageThreshold?.first == undefined
  }

  const [blockAccesPlus, setBlockAccesPlus] = useState(false)

  const updateHolderMutation = useUpdateHolder(projectId, holderId)

  const debouncedOnHolderRequestChange = useDebouncedCallback(() => {
    if (isHolderRequestFilled()) {
      updateHolderMutation.mutate(
        { ...holderRequest, roleId: holderId },
        {
          onSuccess: result => {
            refs.current.initialHolderRequest = result
            setMillesimeAccesPlus(result.millesimeAccesPlus)
          }
        }
      )
    }
  }, LONG_UPDATE_DEBOUNCE_DEFAULT_DELAY)

  useEffect(() => {
    setBlockAccesPlus(disableAccesPlus())
  }, [vintageThreshold, holderRequest?.parcoursAccesPlus])

  useEffect(() => {
    onAccesPlusChange(holderRequest?.parcoursAccesPlus ?? false)
    debouncedOnHolderRequestChange()
  }, [holderRequest])

  useEffect(getFlushEffectCallback(debouncedOnHolderRequestChange), [
    debouncedOnHolderRequestChange
  ])

  const isHolderRequestFilled = () => {
    return (
      !_.isEqual(holderRequest, {}) &&
      !_.isEqual(refs.current.initialHolderRequest, holderRequest) &&
      holderRequest.dateEntreeAccompagnement
    )
  }

  return (
    <Space direction="vertical" className="full-width-space">
      <AppMetierTitle level={4}>Informations Accès +</AppMetierTitle>
      <Descriptions bordered size="middle" column={1}>
        <Descriptions.Item
          labelStyle={LABEL_STYLE}
          label={<Typography.Text strong>Parcours Accès + *</Typography.Text>}
        >
          <AppMetierSwitch
            checked={holderRequest?.parcoursAccesPlus ?? false}
            label=""
            disabled={blockAccesPlus}
            onChange={() => {
              setHolderRequest({
                ...holderRequest,
                parcoursAccesPlus: !holderRequest?.parcoursAccesPlus
              })
            }}
            checkedInnerLabel="Oui"
            uncheckedInnerLabel="Non"
          />
        </Descriptions.Item>
        {holderRequest.parcoursAccesPlus && (
          <>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Date d'entrée en accompagnement*</Typography.Text>}
            >
              {areVintagesLoading ? (
                <Loader />
              ) : (
                <AppMetierDateInput
                  minDate={
                    vintageThreshold ? dayjs(vintageThreshold.first).subtract(1, "year") : undefined
                  }
                  maxDate={
                    vintageThreshold ? dayjs(vintageThreshold.second).add(1, "day") : undefined
                  }
                  status={!holderRequest.dateEntreeAccompagnement ? "error" : undefined}
                  disabled={disableAccesPlusDatePicker()}
                  value={
                    holderRequest?.dateEntreeAccompagnement
                      ? dayjs(holderRequest?.dateEntreeAccompagnement)
                      : undefined
                  }
                  onChange={value =>
                    setHolderRequest({
                      ...holderRequest,
                      dateEntreeAccompagnement: value
                        ? formatDayjs(value, SERVER_DATE_FORMAT)
                        : undefined
                    })
                  }
                  onCleared={() => {
                    setHolderRequest({ ...holderRequest, dateEntreeAccompagnement: undefined })
                  }}
                  allowClear
                  style={{ width: "100%" }}
                />
              )}
              {!holderRequest.dateEntreeAccompagnement && <RequiredFieldMessage />}
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Majeur inscrit à une mission locale</Typography.Text>}
            >
              <AppMetierSwitch
                checked={holderRequest?.majeurInscritMissionLocale ?? false}
                label=""
                onChange={() => {
                  setHolderRequest({
                    ...holderRequest,
                    majeurInscritMissionLocale: !holderRequest?.majeurInscritMissionLocale
                  })
                }}
                checkedInnerLabel="Oui"
                uncheckedInnerLabel="Non"
              />
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Inscrit à Cap Emploi </Typography.Text>}
            >
              <AppMetierSwitch
                checked={holderRequest?.inscritCapEmploi ?? false}
                label=""
                onChange={() => {
                  setHolderRequest({
                    ...holderRequest,
                    inscritCapEmploi: !holderRequest?.inscritCapEmploi
                  })
                }}
                checkedInnerLabel="Oui"
                uncheckedInnerLabel="Non"
              />
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Sortant de l'ASE </Typography.Text>}
            >
              <AppMetierSwitch
                checked={holderRequest?.sortantAse ?? false}
                label=""
                onChange={() => {
                  setHolderRequest({
                    ...holderRequest,
                    sortantAse: !holderRequest?.sortantAse
                  })
                }}
                checkedInnerLabel="Oui"
                uncheckedInnerLabel="Non"
              />
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Parent isolé</Typography.Text>}
            >
              <AppMetierSwitch
                checked={holderRequest?.parentIsole ?? false}
                label=""
                onChange={() => {
                  setHolderRequest({
                    ...holderRequest,
                    parentIsole: !holderRequest?.parentIsole
                  })
                }}
                checkedInnerLabel="Oui"
                uncheckedInnerLabel="Non"
              />
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Personne sans hébergement/hébergée</Typography.Text>}
            >
              <AppMetierSwitch
                checked={holderRequest?.personneSansDomicileHebergee ?? false}
                label=""
                onChange={() => {
                  setHolderRequest({
                    ...holderRequest,
                    personneSansDomicileHebergee: !holderRequest?.personneSansDomicileHebergee
                  })
                }}
                checkedInnerLabel="Oui"
                uncheckedInnerLabel="Non"
              />
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Personne récemment arrivée en France</Typography.Text>}
            >
              <AppMetierSwitch
                checked={holderRequest?.personneRecemmentArriveeFrance ?? false}
                label=""
                onChange={() => {
                  setHolderRequest({
                    ...holderRequest,
                    personneRecemmentArriveeFrance: !holderRequest?.personneRecemmentArriveeFrance
                  })
                }}
                checkedInnerLabel="Oui"
                uncheckedInnerLabel="Non"
              />
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Accompagnement Post Création</Typography.Text>}
            >
              <AppMetierSwitch
                checked={holderRequest?.accompagnementPostCreation ?? false}
                label=""
                onChange={() => {
                  setHolderRequest({
                    ...holderRequest,
                    accompagnementPostCreation: !holderRequest?.accompagnementPostCreation
                  })
                }}
                checkedInnerLabel="Oui"
                uncheckedInnerLabel="Non"
              />
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Précision si niveau 4</Typography.Text>}
            >
              <ReferenceSelect
                referenceCode={ReferenceCode.PRECISION_NIVEAU_QUATRE}
                code={holderRequest.precisionNiveauQuatre?.code}
                onChange={(value?: CodeLibelle) => {
                  setHolderRequest({ ...holderRequest, precisionNiveauQuatre: value })
                }}
              />
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Date d'abandon / sortie</Typography.Text>}
            >
              <AppMetierDateInput
                value={holderRequest?.dateSortie ? dayjs(holderRequest?.dateSortie) : undefined}
                onChange={value =>
                  setHolderRequest({
                    ...holderRequest,
                    dateSortie: value ? formatDayjs(value, SERVER_DATE_FORMAT) : undefined
                  })
                }
                onCleared={() => {
                  setHolderRequest({ ...holderRequest, dateSortie: undefined })
                }}
                allowClear
                style={{ width: "100%" }}
              />
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Motif d'abandon / sortie</Typography.Text>}
            >
              <ReferenceSelect
                referenceCode={ReferenceCode.MOTIF_ABANDON_ACCES_PLUS}
                refList={
                  motifAbandonRefList?.map(m => ({
                    code: m.code,
                    libelle: `${m.ordre} - ${m.libelle}`
                  })) || []
                }
                code={holderRequest.motifSortie?.code}
                onChange={(value?: CodeLibelle) => {
                  setHolderRequest({ ...holderRequest, motifSortie: value })
                }}
              />
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Prescription en cas de sortie</Typography.Text>}
            >
              <ReferenceSelect
                referenceCode={ReferenceCode.PRESCRIPTION_CAS_SORTIE}
                code={holderRequest.prescriptionSortie?.code}
                onChange={(value?: CodeLibelle) => {
                  setHolderRequest({ ...holderRequest, prescriptionSortie: value })
                }}
              />
            </Descriptions.Item>
            <Descriptions.Item
              labelStyle={LABEL_STYLE}
              label={<Typography.Text strong>Millésime Accès +</Typography.Text>}
            >
              {millesimeAccesPlus ?? ""}
            </Descriptions.Item>
          </>
        )}
      </Descriptions>
    </Space>
  )
}
